import clsx from 'clsx'
import { useFormik } from 'formik'
import React, { useMemo } from 'react'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import { useMutation, useQuery } from '@apollo/client'
import { CREATE_FORM_BUILDER } from '../../../../graphql/mutation'
import { GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID } from '../../../../graphql/query'
import { getFormPreviewStats, parseFormDataSource } from './formPreviewUtils'
import { getFormTypeLabel } from './formTypeUtils'
import './CreateFormModal.scss'

interface Props {
  show: boolean
  formInfo: any
  onClose: () => void
}

const DuplicateFormSchema = Yup.object().shape({
  formName: Yup.string()
    .trim()
    .min(2, 'Minimum 2 characters')
    .max(50, 'Maximum 50 characters')
    .matches(
      /^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/,
      'Name must contain at least one non-white space character'
    )
    .required('Name is required'),
  formDescription: Yup.string()
    .trim()
    .min(2, 'Minimum 2 characters')
    .max(100, 'Maximum 100 characters')
    .matches(
      /^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/,
      'Description must contain at least one non-white space character'
    )
    .required('Description is required')
})

const buildCopyName = (name: string) => {
  const base = (name || 'Form').trim()
  const copyLabel = `Copy of ${base}`
  return copyLabel.length <= 50 ? copyLabel : copyLabel.slice(0, 50)
}

const buildCopyDescription = (description: string | undefined, name: string) => {
  const base = description?.trim() || `Copy of ${name || 'form'}`
  return base.length <= 100 ? base : base.slice(0, 100)
}

const cloneFormData = (formData: unknown) => {
  if (formData == null) return formData
  try {
    return JSON.parse(JSON.stringify(formData))
  } catch {
    return formData
  }
}

const DuplicateFormModal: React.FC<Props> = ({ show, formInfo, onClose }) => {
  const { data: fetchedData, loading } = useQuery(GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID, {
    variables: { input: { formUuid: formInfo?.formUuid } },
    context: { headers: { 'access-control-allow-methods': 'Form-Builder' } },
    skip: !show || !formInfo?.formUuid,
    fetchPolicy: 'network-only'
  })

  const resolvedRecord = fetchedData?.getAllFormBuilderByBgAccountId?.data?.[0] ?? formInfo
  const sourceName = resolvedRecord?.name || formInfo?.name || 'Form'
  const sourceFormData = resolvedRecord?.formData ?? formInfo?.formData
  const stats = useMemo(() => getFormPreviewStats(sourceFormData), [sourceFormData])

  const [createFormBuilder] = useMutation(CREATE_FORM_BUILDER, {
    refetchQueries: [
      {
        query: GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID,
        variables: {
          input: {
            formUuid: null
          }
        },
        context: {
          headers: {
            'access-control-allow-methods': 'Form-Builder'
          }
        }
      }
    ],
    context: {
      headers: {
        'access-control-allow-methods': 'Form-Builder'
      }
    }
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      formName: buildCopyName(sourceName),
      formDescription: buildCopyDescription(resolvedRecord?.description, sourceName)
    },
    validationSchema: DuplicateFormSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      const clonedFormData = cloneFormData(parseFormDataSource(sourceFormData))
      if (!clonedFormData) {
        toast.error('Unable to copy form data from the source form.')
        setSubmitting(false)
        return
      }

      try {
        const res = await createFormBuilder({
          variables: {
            input: {
              templateID: resolvedRecord?.templateID ?? 0,
              name: values.formName,
              formData: clonedFormData,
              description: values.formDescription,
              expiryDate: null,
              cardBackground: resolvedRecord?.cardBackground || '',
              cardStyle: resolvedRecord?.cardStyle || resolvedRecord?.cardstyle || '',
              isPrefillForm: !!resolvedRecord?.isPrefillForm,
              prefillRequiresApproval: resolvedRecord?.prefillRequiresApproval !== false
            }
          }
        })

        if (res?.data?.createFormBuilder?.message) {
          toast.success(res.data.createFormBuilder.message)
          resetForm()
          onClose()
        } else {
          toast.error('Unable to duplicate form.')
        }
      } catch (err) {
        console.error(err)
        toast.error('Sorry! Unexpected Error.')
      } finally {
        setSubmitting(false)
      }
    }
  })

  const handleClose = () => {
    formik.resetForm()
    onClose()
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size='lg'
      className='create-form-modal'
      backdrop='static'
    >
      <Modal.Header closeButton className='border-0 pb-0'>
        <div className='d-flex align-items-center gap-3'>
          <div className='create-form-modal__header-icon'>
            <i className='bi bi-copy' />
          </div>
          <div>
            <h2 className='create-form-modal__title'>Duplicate Form</h2>
            <p className='create-form-modal__subtitle'>
              Create a copy of &ldquo;{sourceName}&rdquo; with a new name
            </p>
          </div>
        </div>
      </Modal.Header>

      <form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <div className='create-form-modal__section'>
            <span className='create-form-modal__section-label'>Basic details</span>

            <div className='create-form-modal__field'>
              <label className='create-form-modal__label' htmlFor='duplicate-form-name'>
                <span>New form name</span>
                <span className='create-form-modal__char-count'>
                  {formik.values.formName.length}/50
                </span>
              </label>
              <input
                id='duplicate-form-name'
                {...formik.getFieldProps('formName')}
                className={clsx('form-control create-form-modal__input', {
                  'is-invalid': formik.touched.formName && formik.errors.formName
                })}
                type='text'
                placeholder='e.g. Customer Feedback Survey Copy'
                autoComplete='off'
                maxLength={50}
                disabled={loading}
              />
              {formik.touched.formName && formik.errors.formName && (
                <div className='create-form-modal__error' role='alert'>
                  {formik.errors.formName}
                </div>
              )}
            </div>

            <div className='create-form-modal__field'>
              <label className='create-form-modal__label' htmlFor='duplicate-form-description'>
                <span>Description</span>
                <span className='create-form-modal__char-count'>
                  {formik.values.formDescription.length}/100
                </span>
              </label>
              <textarea
                id='duplicate-form-description'
                {...formik.getFieldProps('formDescription')}
                className={clsx('form-control create-form-modal__textarea', {
                  'is-invalid': formik.touched.formDescription && formik.errors.formDescription
                })}
                placeholder='Briefly describe what this form is for'
                autoComplete='off'
                maxLength={100}
                disabled={loading}
              />
              {formik.touched.formDescription && formik.errors.formDescription && (
                <div className='create-form-modal__error' role='alert'>
                  {formik.errors.formDescription}
                </div>
              )}
            </div>
          </div>

          <div className='create-form-modal__section mb-0'>
            <span className='create-form-modal__section-label'>Form data to copy</span>
            <div className='alert alert-light border mb-0'>
              {loading ? (
                <div className='d-flex align-items-center gap-2 text-muted'>
                  <span
                    className='spinner-border spinner-border-sm'
                    role='status'
                    aria-hidden='true'
                  />
                  Loading form data...
                </div>
              ) : (
                <>
                  <p className='mb-2 mb-md-3'>
                    All fields, pages, and layout settings from the original form will be copied to
                    the new form.
                  </p>
                  <div className='d-flex flex-wrap gap-3'>
                    <span>
                      <strong>{stats.pageCount}</strong> page{stats.pageCount === 1 ? '' : 's'}
                    </span>
                    <span>
                      <strong>{stats.inputFieldCount}</strong> input field
                      {stats.inputFieldCount === 1 ? '' : 's'}
                    </span>
                    <span>
                      Type: <strong>{getFormTypeLabel(resolvedRecord ?? formInfo)}</strong>
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button type='button' className='btn create-form-modal__btn-cancel' onClick={handleClose}>
            Cancel
          </button>
          <button
            type='submit'
            className='btn btn-primary create-form-modal__btn-submit'
            disabled={formik.isSubmitting || loading || !formik.isValid || !sourceFormData}
          >
            {formik.isSubmitting ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                />
                Duplicating...
              </>
            ) : (
              <>
                <i className='bi bi-copy me-2' />
                Duplicate Form
              </>
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default DuplicateFormModal
